'use client';

import React, { useState } from 'react';
import {
    UserCircle2,
    Plus,
    Search,
    Filter,
    ChevronDown,
    Edit,
    Trash2,
    Eye,
    Key,
    ShieldCheck,
    Lock,
    X,
    UserCheck,
    Mail,
    Calendar,
    School,
    CheckCircle2,
    XCircle,
    Copy,
    Send
} from 'lucide-react';
import Link from 'next/link';

// --- Types ---

type UserStatus = 'Aktif' | 'Nonaktif';
type UserRole = 'Admin Sistem' | 'Kepala Sekolah' | 'Bendahara' | 'Tata Usaha' | 'Operator Keuangan' | 'Viewer';

interface UserAccount {
    id: string;
    name: string;
    email: string;
    username: string;
    role: UserRole;
    unit: string;
    status: UserStatus;
    lastLogin: string;
    hasTransactions: boolean; // Cannot delete if true
}

// --- Mock Data ---

const INITIAL_USERS: UserAccount[] = [
    {
        id: '1',
        name: 'Ahmad Fauzi',
        email: 'ahmad.fauzi@azhar1.sch.id',
        username: 'afauzi_admin',
        role: 'Admin Sistem',
        unit: 'PAUD Al-Azhar 1',
        status: 'Aktif',
        lastLogin: '2024-02-12 08:30',
        hasTransactions: true
    },
    {
        id: '2',
        name: 'Dr. Siti Aminah',
        email: 'siti.aminah@azhar1.sch.id',
        username: 'sitis_kepsek',
        role: 'Kepala Sekolah',
        unit: 'PAUD Al-Azhar 1',
        status: 'Aktif',
        lastLogin: '2024-02-11 14:15',
        hasTransactions: false
    },
    {
        id: '3',
        name: 'Budi Santoso',
        email: 'budi.santoso@azhar1.sch.id',
        username: 'bsant_bendahara',
        role: 'Bendahara',
        unit: 'PAUD Al-Azhar 1',
        status: 'Aktif',
        lastLogin: '2024-02-12 09:45',
        hasTransactions: true
    },
    {
        id: '4',
        name: 'Rina Wijaya',
        email: 'rina.wijaya@azhar1.sch.id',
        username: 'rwijaya_staff',
        role: 'Tata Usaha',
        unit: 'PAUD Al-Azhar 1',
        status: 'Nonaktif',
        lastLogin: '2024-01-20 11:20',
        hasTransactions: false
    }
];

export default function ManajemenAkunPage() {
    // --- State ---
    const [users, setUsers] = useState<UserAccount[]>(INITIAL_USERS);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');

    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserAccount | null>(null);

    // --- Action Handlers ---
    const handleAddClick = () => {
        setSelectedUser(null);
        setIsFormModalOpen(true);
    };

    const handleEditClick = (user: UserAccount) => {
        setSelectedUser(user);
        setIsFormModalOpen(true);
    };

    const handleViewDetail = (user: UserAccount) => {
        setSelectedUser(user);
        setIsDetailModalOpen(true);
    };

    const handleResetPassword = (user: UserAccount) => {
        setSelectedUser(user);
        setIsResetModalOpen(true);
    };

    const handleDelete = (id: string) => {
        if (confirm('Apakah Anda yakin ingin menghapus akun ini?')) {
            setUsers(prev => prev.filter(u => u.id !== id));
        }
    };

    // --- Filter logic ---
    const filteredUsers = users.filter(user => {
        const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.username.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesRole = roleFilter === 'all' || user.role === roleFilter;
        const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
        return matchesSearch && matchesRole && matchesStatus;
    });

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                            <Link href="/sekolah" className="hover:text-primary transition-all">Master Data</Link>
                            <span className="text-muted/50">•</span>
                            <span className="text-foreground">Manajemen Akun</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <UserCircle2 className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight whitespace-nowrap">Manajemen Akun</h1>
                                <p className="text-secondary font-medium flex items-center gap-2 text-sm uppercase tracking-wider">
                                    <UserCheck className="w-3.5 h-3.5 text-primary" /> Kelola Pengguna Sistem Sekolah
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleAddClick}
                        className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all"
                    >
                        <Plus className="w-4 h-4" /> Tambah Akun Pengguna
                    </button>
                </div>
            </section>

            {/* Filter & Search */}
            <div className="bg-surface border border-border-main p-4 rounded-3xl shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="relative flex-1 group w-full md:max-w-xs transition-all">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Cari Nama / Email / Username..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-background/50 border border-border-main rounded-2xl py-3.5 pl-12 pr-6 text-xs font-semibold focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all outline-none"
                        />
                    </div>

                    <div className="flex flex-wrap gap-3 flex-1">
                        <div className="relative group min-w-[140px]">
                            <select
                                value={roleFilter}
                                onChange={(e) => setRoleFilter(e.target.value)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Semua Role</option>
                                <option>Admin Sistem</option>
                                <option>Kepala Sekolah</option>
                                <option>Bendahara</option>
                                <option>Tata Usaha</option>
                                <option>Viewer</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        <div className="relative group min-w-[140px]">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Semua Status</option>
                                <option value="Aktif">Aktif</option>
                                <option value="Nonaktif">Nonaktif</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-surface border border-border-main rounded-3xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Nama Pengguna</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Email / Username</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[180px]">Role / Unit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[120px] text-center">Status</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px] text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {filteredUsers.length > 0 ? filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-primary-soft/5 transition-colors group">
                                    <td className="px-6 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-primary font-black text-xs shadow-sm group-hover:bg-primary-soft/30 transition-all uppercase">
                                                {user.name.split(' ').map(n => n[0]).join('')}
                                            </div>
                                            <div className="space-y-0.5">
                                                <p className="text-xs font-black text-foreground">{user.name}</p>
                                                <p className="text-[10px] font-bold text-muted flex items-center gap-2 uppercase tracking-tighter">
                                                    <Calendar className="w-3 h-3" /> Login: {user.lastLogin}
                                                </p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-2 group/email cursor-pointer">
                                                <Mail className="w-3 h-3 text-muted group-hover/email:text-primary transition-colors" />
                                                <p className="text-xs font-medium text-secondary group-hover/email:text-primary decoration-primary/30 transition-all">{user.email}</p>
                                            </div>
                                            <p className="text-[10px] font-black text-muted uppercase tracking-widest font-mono pl-5">@{user.username}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5">
                                        <div className="space-y-1">
                                            <span className="px-2 py-0.5 bg-primary-soft text-primary border border-primary/10 rounded text-[9px] font-black uppercase tracking-wider">{user.role}</span>
                                            <p className="text-[9px] font-bold text-muted uppercase tracking-widest flex items-center gap-1.5 pl-1">
                                                <School className="w-3 h-3 text-muted/50" /> {user.unit}
                                            </p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${user.status === 'Aktif' ? 'bg-success-soft text-success border-success/10' : 'bg-muted/10 text-muted border-muted/10'
                                            }`}>
                                            <div className={`w-1.5 h-1.5 rounded-full ${user.status === 'Aktif' ? 'bg-success' : 'bg-muted'}`}></div>
                                            {user.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                            <button onClick={() => handleViewDetail(user)} className="p-2 rounded-xl text-muted hover:text-primary hover:bg-primary-soft transition-all" title="Detail"><Eye className="w-4 h-4" /></button>
                                            <button onClick={() => handleEditClick(user)} className="p-2 rounded-xl text-muted hover:text-warning hover:bg-warning-soft transition-all" title="Edit"><Edit className="w-4 h-4" /></button>
                                            <button onClick={() => handleResetPassword(user)} className="p-2 rounded-xl text-muted hover:text-secondary hover:bg-secondary-soft transition-all" title="Reset Password"><Key className="w-4 h-4" /></button>
                                            <button
                                                onClick={() => handleDelete(user.id)}
                                                disabled={user.hasTransactions}
                                                className="p-2 rounded-xl text-muted hover:text-error hover:bg-error-soft transition-all disabled:opacity-20"
                                                title={user.hasTransactions ? "Pernah Ber-transaksi (Tidak bisa hapus)" : "Hapus Akun"}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={5} className="px-6 py-10 text-center text-muted font-medium italic animate-pulse">
                                        Data tidak ditemukan...
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Form Modal (Add/Edit) */}
            {isFormModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface rounded-4xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-border-main bg-primary-soft/5 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white rounded-2xl border border-primary/10 flex items-center justify-center text-primary shadow-sm">
                                    {selectedUser ? <Edit className="w-6 h-6" /> : <Plus className="w-6 h-6" />}
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-foreground">{selectedUser ? 'Edit Akun Pengguna' : 'Tambah Akun Baru'}</h3>
                                    <p className="text-[10px] font-black text-primary uppercase tracking-widest">Keamanan Sistem</p>
                                </div>
                            </div>
                            <button onClick={() => setIsFormModalOpen(false)} className="p-2 rounded-full hover:bg-secondary-soft/50 text-muted transition-all"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="p-8 overflow-y-auto space-y-6 flex-1 custom-scrollbar">
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Nama Lengkap</label>
                                    <input
                                        type="text"
                                        placeholder="Contoh: Siti Aminah"
                                        defaultValue={selectedUser?.name}
                                        className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all font-mono"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Email Sekolah</label>
                                        <input
                                            type="email"
                                            placeholder="user@sekolah.sch.id"
                                            defaultValue={selectedUser?.email}
                                            className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Username</label>
                                        <input
                                            type="text"
                                            placeholder="username_sistem"
                                            defaultValue={selectedUser?.username}
                                            className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Role / Hak Akses</label>
                                        <div className="relative">
                                            <select
                                                defaultValue={selectedUser?.role}
                                                className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-bold outline-none appearance-none cursor-pointer focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all"
                                            >
                                                <option>Admin Sistem</option>
                                                <option>Kepala Sekolah</option>
                                                <option>Bendahara</option>
                                                <option>Tata Usaha</option>
                                                <option>Viewer</option>
                                            </select>
                                            <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Unit Penugasan</label>
                                        <div className="relative">
                                            <select
                                                defaultValue={selectedUser?.unit}
                                                className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-bold outline-none appearance-none cursor-pointer focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all"
                                            >
                                                <option>PAUD Al-Azhar 1</option>
                                                <option>SD Al-Azhar 1</option>
                                                <option>SMP Al-Azhar 1</option>
                                            </select>
                                            <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Status Keaktifan</label>
                                    <div className="flex gap-4">
                                        <label className="flex-1 cursor-pointer">
                                            <input type="radio" name="status" value="Aktif" defaultChecked={selectedUser?.status === 'Aktif' || !selectedUser} className="peer hidden" />
                                            <div className="flex items-center justify-center p-3 rounded-2xl border border-border-main bg-background text-muted peer-checked:bg-success peer-checked:text-white peer-checked:border-success transition-all font-black text-[10px] uppercase tracking-widest">
                                                Aktif
                                            </div>
                                        </label>
                                        <label className="flex-1 cursor-pointer">
                                            <input type="radio" name="status" value="Nonaktif" defaultChecked={selectedUser?.status === 'Nonaktif'} className="peer hidden" />
                                            <div className="flex items-center justify-center p-3 rounded-2xl border border-border-main bg-background text-muted peer-checked:bg-error peer-checked:text-white peer-checked:border-error transition-all font-black text-[10px] uppercase tracking-widest">
                                                Nonaktif
                                            </div>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {selectedUser?.hasTransactions && (
                                <div className="p-4 bg-muted/5 border border-border-main border-dashed rounded-2xl flex gap-3 italic">
                                    <ShieldCheck className="w-5 h-5 text-success shrink-0" />
                                    <p className="text-[10px] font-bold text-muted leading-relaxed uppercase tracking-tight">Akun ini memiliki histori transaksi. Hanya status keaktifan yang dapat dinonaktifkan demi integritas audit.</p>
                                </div>
                            )}
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex gap-4">
                            <button onClick={() => setIsFormModalOpen(false)} className="px-8 py-4 bg-white border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft/50 transition-all flex-1">Batal</button>
                            <button onClick={() => setIsFormModalOpen(false)} className="px-8 py-4 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all flex-1">Simpan Pengguna</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reset Password Modal */}
            {isResetModalOpen && selectedUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface p-10 rounded-4xl shadow-2xl max-w-sm w-full space-y-6 animate-in zoom-in-95 duration-200 text-center">
                        <div className="w-20 h-20 bg-primary-soft text-primary rounded-3xl flex items-center justify-center mx-auto shadow-inner group">
                            <Key className="w-10 h-10 group-hover:rotate-12 transition-transform" />
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-xl font-black text-foreground uppercase tracking-tight">Reset Password?</h3>
                            <p className="text-xs font-medium text-secondary leading-relaxed">
                                Password baru akan dibuat secara otomatis dan dikirimkan ke email <br />
                                <span className="font-black text-primary decoration-primary/30 underline decoration-2 underline-offset-4">{selectedUser.email}</span>
                            </p>
                        </div>
                        <div className="flex flex-col gap-3">
                            <button onClick={() => setIsResetModalOpen(false)} className="w-full py-4 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.05] transition-all flex items-center justify-center gap-2">
                                <Send className="w-4 h-4" /> Reset & Kirim Email
                            </button>
                            <button onClick={() => setIsResetModalOpen(false)} className="w-full py-4 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft transition-all">Batal</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Detail Modal */}
            {isDetailModalOpen && selectedUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface rounded-5xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="relative h-32 bg-primary">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-2xl"></div>
                            <button onClick={() => setIsDetailModalOpen(false)} className="absolute top-6 right-6 p-2 bg-white/20 hover:bg-white/40 text-white rounded-full transition-all backdrop-blur-md">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="px-10 pb-12 -mt-16 relative z-10 text-center space-y-6">
                            <div className="w-32 h-32 rounded-[40px] bg-white border-8 border-surface p-2 mx-auto shadow-2xl group transition-all">
                                <div className="w-full h-full rounded-[32px] bg-primary text-white flex items-center justify-center text-4xl font-black uppercase">
                                    {selectedUser.name[0]}
                                </div>
                            </div>

                            <div className="space-y-2 pt-2">
                                <h3 className="text-2xl font-black text-foreground tracking-tight">{selectedUser.name}</h3>
                                <div className="flex items-center justify-center gap-3">
                                    <span className="px-3 py-1 bg-primary-soft text-primary border border-primary/10 rounded-full text-[10px] font-black uppercase tracking-widest">{selectedUser.role}</span>
                                    <span className="w-1.5 h-1.5 rounded-full bg-muted/40"></span>
                                    <span className="text-[10px] font-black text-muted uppercase tracking-widest">ID: {selectedUser.id}</span>
                                </div>
                            </div>

                            <div className="space-y-3 pt-4">
                                <div className="flex items-center justify-between p-4 bg-background border border-border-main rounded-2xl group active:scale-95 transition-all">
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-4 h-4 text-muted" />
                                        <span className="text-xs font-bold text-secondary">{selectedUser.email}</span>
                                    </div>
                                    <Copy className="w-4 h-4 text-muted group-hover:text-primary cursor-pointer transition-colors" />
                                </div>
                                <div className="flex items-center justify-between p-4 bg-background border border-border-main rounded-2xl transition-all">
                                    <div className="flex items-center gap-3">
                                        <School className="w-4 h-4 text-muted" />
                                        <span className="text-xs font-bold text-secondary">{selectedUser.unit}</span>
                                    </div>
                                    <span className="text-[10px] font-black text-primary uppercase tracking-widest">Home Unit</span>
                                </div>
                                <div className="flex items-center justify-between p-4 bg-background border border-border-main rounded-2xl transition-all">
                                    <div className="flex items-center gap-3">
                                        <Calendar className="w-4 h-4 text-muted" />
                                        <span className="text-xs font-bold text-secondary">Aktivitas Terakhir</span>
                                    </div>
                                    <span className="text-[10px] font-black text-muted uppercase tracking-tighter">{selectedUser.lastLogin}</span>
                                </div>
                            </div>

                            <div className="pt-6">
                                <div className={`flex items-center justify-center gap-3 p-4 rounded-3xl border ${selectedUser.status === 'Aktif' ? 'bg-success-soft/20 border-success/10 text-success' : 'bg-error-soft/20 border-error/10 text-error'}`}>
                                    {selectedUser.status === 'Aktif' ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em]">Status Akun: {selectedUser.status}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
